import { FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ActiveCPI, AvailableCPI } from '../../categoryDashboard.types'
import { CPIsWrapper, CPISkeletonCard } from './cpisSection.styles'
import ActiveCPICard from './ActiveCPICard'
import AvailableCPICard from './AvailableCPICard'
import { CPIsIcon } from '../icons'
import ThemeButton from 'ui/components/buttons/primary/ThemeButton'
import { buildLibraryLink } from 'ui/pages/library/library.filters'

type CPIsTab = 'active' | 'available'

interface Props {
  activeCPIs: ActiveCPI[]
  availableCPIs: AvailableCPI[]
  lastUpdate: string
  fillHeight?: boolean
  loading?: boolean
  availableLoading?: boolean
  benchmarkingLoading?: boolean
  recentFindingLoading?: boolean
  activeTab: CPIsTab
  onTabChange: (tab: CPIsTab) => void
  onCpiActivated?: () => void | Promise<void>
}

const CPIsSection: FC<Props> = ({
  activeCPIs,
  availableCPIs,
  lastUpdate,
  fillHeight,
  loading,
  availableLoading,
  benchmarkingLoading,
  recentFindingLoading,
  activeTab,
  onTabChange,
  onCpiActivated
}) => {
  const navigate = useNavigate()
  const { categoryName } = useParams<{ categoryName: string }>()
  const currentItems = activeTab === 'active' ? activeCPIs : availableCPIs
  // Available CPIs are fetched separately from the performance score (active
  // CPIs), so the Available tab needs to consider its own loading flag —
  // otherwise switching to it before that fetch resolves briefly flashes the
  // empty state.
  const isTabLoading = loading || (activeTab === 'available' && availableLoading)
  const isEmpty = currentItems.length === 0

  const handleActivateCpi = () => {
    navigate(buildLibraryLink(categoryName))
  }

  return (
    <CPIsWrapper sx={fillHeight ? { flex: 1 } : undefined}>
      <div className="section-header">
        <div className="header-left">
          <div className="title-row">
            <CPIsIcon className="title-icon" />
            <span className="title-text">CPIs</span>
          </div>
          <div className="last-update">
            Last Update: <span className="date-value">{lastUpdate}</span>
          </div>
        </div>

        <div className="toggle-container">
          <button
            className={`toggle-button ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => onTabChange('active')}
          >
            Active ({activeCPIs.length})
          </button>
          <button
            className={`toggle-button ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => onTabChange('available')}
          >
            Available ({availableCPIs.length})
          </button>
        </div>
      </div>

      {isTabLoading ? (
        <div className="cards-grid">
          {Array.from({ length: 6 }, (_, i) => (
            <CPISkeletonCard key={i} />
          ))}
        </div>
      ) : isEmpty ? (
        <div className="empty-state">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 17.5C4 16.6716 4.67157 16 5.5 16H8.5C9.32843 16 10 16.6716 10 17.5V26.5C10 27.3284 9.32843 28 8.5 28H5.5C4.67157 28 4 27.3284 4 26.5V17.5Z"
              stroke="#A88CA4"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 11.5C13 10.6716 13.6716 10 14.5 10H17.5C18.3284 10 19 10.6716 19 11.5V26.5C19 27.3284 18.3284 28 17.5 28H14.5C13.6716 28 13 27.3284 13 26.5V11.5Z"
              stroke="#A88CA4"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 5.5C22 4.67157 22.6716 4 23.5 4H26.5C27.3284 4 28 4.67157 28 5.5V26.5C28 27.3284 27.3284 28 26.5 28H23.5C22.6716 28 22 27.3284 22 26.5V5.5Z"
              stroke="#A88CA4"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="empty-title">No CPI Data Available</span>
          <span className="empty-description">Activate CPI to start generating performance data</span>
          <ThemeButton
            themevariant="primary"
            shape="rectangular"
            onClick={handleActivateCpi}
            sx={{ marginTop: '32px' }}
          >
            Activate CPI
          </ThemeButton>
        </div>
      ) : (
        <div className="cards-grid">
          {activeTab === 'active'
            ? activeCPIs.map((cpi) => (
                <ActiveCPICard
                  key={cpi.cpiId}
                  cpi={cpi}
                  benchmarkingLoading={benchmarkingLoading}
                  recentFindingLoading={recentFindingLoading}
                />
              ))
            : availableCPIs.map((cpi) => (
                <AvailableCPICard key={cpi.cpiId} cpi={cpi} onActivated={onCpiActivated} />
              ))}
        </div>
      )}
    </CPIsWrapper>
  )
}

export default CPIsSection
